import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useHistory } from "react-router-dom";
import NewSupplierModal from './NewSupplierModal';
import swal from 'sweetalert';
import axios from '../../../../../services/AxiosInstance';
import { Card, Badge, Button, Spinner, Row, Col, Alert } from "react-bootstrap";
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import PageHeader from "../../../../layouts/PageHeader";
import SupplierInvoiceTable from '../../supplier-invoices/SupplierInvoiceTable';
import { SupplierPaymentMethodsCard } from '../../../Banking/payments/SupplierPaymentMethods';
import { getApiUrl } from '../../../../../utils/ApiUtils';
// Lucide React Icon
import {
  Building,
  MapPin,
  Phone,
  Globe,
  Hash,
  CreditCard,
  FileText,
  TrendingUp,
  Edit3,
  Mail,
  Calendar,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Clock,
  BarChart3,
    ShoppingCart
} from 'lucide-react';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const SupplierDetails = () => {
  const { companyId, supplierId } = useParams();
  const history = useHistory();
  const [supplier, setSupplier] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total_invoices: 0,
    pending_invoices: 0,
    paid_invoices: 0,
    overdue_invoices: 0,
    total_amount_due: 0,
    total_amount_paid: 0,
    average_invoice_value: 0,
    recent_activity: []
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [modalKey, setModalKey] = useState(0);

  useEffect(() => {
    fetchAllData();
  }, [supplierId]);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);

    try {
      await Promise.all([
        fetchSupplierDetails(),
        fetchSupplierInvoices(),
        fetchSupplierStats()
      ]);
    } catch (err) {
      setError('Failed to load supplier data');
      console.error('Error loading supplier data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSupplierDetails = async () => {
    const getSupplierDetailsUrl = getApiUrl(`${companyId}/purchasing/suppliers/${supplierId}`);
    const res = await axios.get(getSupplierDetailsUrl, { withCredentials: true });
    setSupplier(res.data);
  };

  const fetchSupplierInvoices = async () => {
    try {
      const getSupplierInvoicesUrl = getApiUrl(`${companyId}/purchasing/suppliers/${supplierId}/invoices`);
      const res = await axios.get(getSupplierInvoicesUrl, { withCredentials: true });

      console.log('🔍 DEBUG - Supplier Invoices API Response:', res.data);

      setInvoices(res.data || []);
    } catch (error) {
      console.error('Error fetching supplier invoices:', error);
      setInvoices([]);
    }
  };

  const fetchSupplierStats = async () => {
    try {
      const getSupplierStatsUrl = getApiUrl(`${companyId}/purchasing/suppliers/${supplierId}/stats`);
      const res = await axios.get(getSupplierStatsUrl, { withCredentials: true });

      console.log('🔍 DEBUG - Supplier Stats API Response:', res.data);

      // Calculate additional metrics
      const enhancedStats = {
        ...res.data,
        average_invoice_value: res.data.total_invoices > 0
          ? (parseFloat(res.data.total_amount_due || 0) + parseFloat(res.data.total_amount_paid || 0)) / res.data.total_invoices
          : 0
      };

      setStats(enhancedStats);
    } catch (error) {
      console.error('Error fetching supplier stats:', error);
      // Set default values if stats fail
      setStats({
        total_invoices: 0,
        pending_invoices: 0,
        paid_invoices: 0,
        overdue_invoices: 0,
        total_amount_due: 0,
        total_amount_paid: 0,
        average_invoice_value: 0
      });
    }
  };

  const handleEditSupplier = useCallback(() => {
    setModalKey(prevKey => prevKey + 1);
    setShowEditModal(true);
  }, []);

  const handleCloseModal = () => {
    setShowEditModal(false);
  };

  const handleUpdate = async (updatedSupplierData) => {
    try {
      const updatedSupplier = {
        supplier_id: supplierId,
        name: updatedSupplierData.name,
        orgnumber: updatedSupplierData.orgnumber,
        vatnumber: updatedSupplierData.vatnumber,
        postadress1: updatedSupplierData.address,
        postadress2: '',
        city: updatedSupplierData.city,
        postnumber: updatedSupplierData.postalcode,
        country: updatedSupplierData.country,
        phonenumber: updatedSupplierData.phonenumber
      };

      const updateSupplierApiUrl = getApiUrl(`${companyId}/purchasing/suppliers/${supplierId}`);
      const response = await axios.put(updateSupplierApiUrl, updatedSupplier, { withCredentials: true });

      if (response.status === 200) {
        swal('Klart!', 'Leverantörsinformationen har uppdaterats.', 'success');
        await fetchAllData(); // Refresh all data
        setShowEditModal(false);
      }
    } catch (error) {
      swal('Error', 'Failed to update supplier', 'error');
      console.error(error);
    }
  };

  // Handle edit invoice - navigate to edit form
  const handleEditInvoice = useCallback((invoiceId) => {
    const invoice = invoices.find(inv => inv.invoice_id === invoiceId);

    // Check if invoice can be edited
    if (invoice?.status === 'Verification Created' || invoice?.status === 'Approved') {
      swal({
        title: 'Kan inte redigera',
        text: 'Fakturan har redan en verifikation och kan inte redigeras. Du kan visa den eller ta bort den om du har behörighet.',
        icon: 'warning'
      });
      return;
    }

    history.push(`/${companyId}/edit-supplier-invoice/${invoiceId}`);
  }, [invoices, companyId, history]);

  // Handle delete invoice
  const handleDeleteInvoice = useCallback(async (invoiceId) => {
    const invoice = invoices.find(inv => inv.invoice_id === invoiceId);

    // Check if invoice can be deleted
    if (invoice?.status === 'Verification Created' || invoice?.status === 'Approved') {
      swal({
        title: 'Kan inte ta bort',
        text: 'Fakturan har redan en verifikation och kan inte tas bort. Ta först bort verifikationen för att kunna ta bort fakturan.',
        icon: 'error'
      });
      return;
    }

    const willDelete = await swal({
      title: 'Är du säker?',
      text: 'Leverantörsfakturan kommer att tas bort permanent.',
      icon: 'warning',
      buttons: ['Avbryt', 'Ta bort'],
      dangerMode: true,
    });

    if (willDelete) {
      try {
        const deleteApiUrl = getApiUrl(`${companyId}/purchasing/supplier-invoices/${invoiceId}`);
        await axios.delete(deleteApiUrl, { withCredentials: true });

        await swal('Borttagen!', 'Fakturan har tagits bort.', 'success');

        // Refresh all data after deletion
        await fetchAllData();
      } catch (error) {
        console.error('Error deleting invoice:', error);
        swal('Fel', 'Kunde inte ta bort fakturan. Försök igen senare.', 'error');
      }
    }
  }, [invoices, companyId, fetchAllData]);

  const invoiceColumns = useMemo(() => [
    {
      Header: 'Id',
      accessor: 'invoice_id',
    },
    {
      Header: 'Fakturanummer',
      accessor: 'invoice_number',
    },
    {
      Header: 'Fakturadatum',
      accessor: 'invoice_date',
    },
    {
      Header: 'Förfallodatum',
      accessor: 'due_date',
    },
    {
      Header: 'Belopp',
      accessor: 'amount',
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: props => <Badge bg={props.value === 'Paid' ? 'success' : props.value === 'Pending' ? 'warning' : 'danger'}>{props.value}</Badge>,
    },
  ], []);

  const chartData = useMemo(() => ({
    labels: ['Väntande', 'Betalda', 'Förfallna'],
    datasets: [
      {
        label: 'Antal fakturor',
        data: [stats.pending_invoices, stats.paid_invoices, stats.overdue_invoices],
        backgroundColor: ['#f59e0b', 'var(--bs-primary)', '#dc2626'],
        borderColor: ['#d97706', 'var(--primary-hover)', '#b91c1c'],
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  }), [stats]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        cornerRadius: 8,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: '#f1f5f9',
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 12,
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 12,
          },
        },
      },
    },
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="text-center">
          <Spinner animation="border" style={{ color: 'var(--bs-primary)' }} className="mb-3" />
          <div style={{ color: '#6b7280' }}>Laddar leverantörsinformation...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <AlertTriangle size={16} className="me-2" />
        {error}
      </Alert>
    );
  }

  return (
    <>
      <style>{`
        .modern-card {
          background: white;
          border: none;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          overflow: hidden;
        }

        .modern-card:hover {
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        }

        .stat-card {
          background: white;
          border: none;
          border-radius: 8px;
          padding: 1.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          transition: all 0.2s ease;
        }

        .stat-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1rem;
        }

        .stat-value {
          font-size: 1.75rem;
          font-weight: 700;
          margin-bottom: 0.25rem;
          line-height: 1.2;
        }

        .stat-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin-bottom: 0.25rem;
        }

        .stat-subtitle {
          font-size: 0.75rem;
          color: #6b7280;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.25rem;
        }

        .info-item {
          background: #f8fafc;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 0.75rem;
          transition: all 0.2s ease;
        }

        .info-item:hover {
          background: white;
          border-color: var(--bs-primary);
          box-shadow: 0 2px 8px var(--rgba-primary-1);
        }

        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          }
        }

        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr 1fr;
          }
        }
      `}</style>

      <PageHeader
        backTo={{ to: `/${companyId}/suppliers`, label: 'Alla leverantörer' }}
        icon={ShoppingCart}
        title="Leverantörsdetaljer"
      />

      <div className="col-12">

        {/* Header Section */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <h2 className="mb-2" style={{ fontWeight: '700', fontSize: '1.75rem' }}>
                {supplier.name}
              </h2>
              <div className="d-flex align-items-center gap-3 flex-wrap">
                {supplier.orgnumber && (
                  <div className="d-flex align-items-center gap-2 text-muted">
                    <Hash size={16} />
                    <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                      Org.nr: {supplier.orgnumber}
                    </span>
                  </div>
                )}
                {supplier.vatnumber && (
                  <div className="d-flex align-items-center gap-2 text-muted">
                    <CreditCard size={16} />
                    <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                      VAT: {supplier.vatnumber}
                    </span>
                  </div>
                )}
                {supplier.city && (
                  <div className="d-flex align-items-center gap-2 text-muted">
                    <MapPin size={16} />
                    <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>{supplier.city}</span>
                  </div>
                )}
                {supplier.phone && (
                  <div className="d-flex align-items-center gap-2 text-muted">
                    <Phone size={16} />
                    <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>{supplier.phone}</span>
                  </div>
                )}
              </div>
            </div>
            <Button
              variant="outline-primary"
              onClick={handleEditSupplier}
              style={{
                borderColor: 'var(--bs-primary)',
                color: 'var(--bs-primary)',
                padding: '0.5rem 1rem',
                fontWeight: '600',
                fontSize: '0.875rem'
              }}
            >
              <Edit3 size={16} className="me-2" />
              Redigera
            </Button>
          </div>
        </div>

        {/* Supplier Address Information */}
        {supplier.postadress1 && (
          <div className="modern-card mb-4">
            <div className="p-4">
              <h5 className="mb-3" style={{ fontWeight: '600', fontSize: '1rem' }}>Adress</h5>
              <div className="info-item">
                <div className="d-flex align-items-start">
                  <MapPin size={18} className="me-2 mt-1" style={{ color: 'var(--bs-primary)' }} />
                  <div>
                    <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>{supplier.postadress1}</div>
                    <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                      {supplier.postnumber} {supplier.city}
                      {supplier.country && `, ${supplier.country}`}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Betalningsuppgifter — where payment files send the money (ISO 20022, plan UC7) */}
        <SupplierPaymentMethodsCard companyId={companyId} supplierId={supplierId} supplierName={supplier.name} />

        {/* Financial Statistics Dashboard */}
        <div className="stats-grid mb-4">
          {/* Total Invoices */}
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#f0f9ff', color: '#0284c7' }}>
              <FileText size={24} />
            </div>
            <div className="stat-value" style={{ color: '#0284c7' }}>
              {stats.total_invoices || 0}
            </div>
            <div className="stat-label">Totala fakturor</div>
            <div className="stat-subtitle">
              Alla fakturor från leverantören
            </div>
          </div>

          {/* Outstanding Amount */}
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#fef2f2', color: '#dc2626' }}>
              <CreditCard size={24} />
            </div>
            <div className="stat-value" style={{ color: '#dc2626' }}>
              {new Intl.NumberFormat('sv-SE', {
                style: 'currency',
                currency: 'SEK',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }).format(stats.total_amount_due || 0)}
            </div>
            <div className="stat-label">Utestående</div>
            <div className="stat-subtitle">
              {stats.pending_invoices || 0} väntande fakturor
            </div>
          </div>

          {/* Paid Amount */}
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#f0fdf4', color: '#059669' }}>
              <CheckCircle size={24} />
            </div>
            <div className="stat-value" style={{ color: '#059669' }}>
              {new Intl.NumberFormat('sv-SE', {
                style: 'currency',
                currency: 'SEK',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }).format(stats.total_amount_paid || 0)}
            </div>
            <div className="stat-label">Betalt totalt</div>
            <div className="stat-subtitle">
              {stats.paid_invoices || 0} betalda fakturor
            </div>
          </div>

          {/* Average Invoice Value */}
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#fefbeb', color: '#f59e0b' }}>
              <TrendingUp size={24} />
            </div>
            <div className="stat-value" style={{ color: '#f59e0b' }}>
              {new Intl.NumberFormat('sv-SE', {
                style: 'currency',
                currency: 'SEK',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }).format(stats.average_invoice_value || 0)}
            </div>
            <div className="stat-label">Snitt fakturavärde</div>
            <div className="stat-subtitle">
              Genomsnittligt belopp per faktura
            </div>
          </div>

          {/* Overdue Invoices */}
          {stats.overdue_invoices > 0 && (
            <div className="stat-card" style={{ borderLeft: '4px solid #dc2626' }}>
              <div className="stat-icon" style={{ background: '#fef2f2', color: '#dc2626' }}>
                <AlertTriangle size={24} />
              </div>
              <div className="stat-value" style={{ color: '#dc2626' }}>
                {stats.overdue_invoices || 0}
              </div>
              <div className="stat-label">Förfallna fakturor</div>
              <div className="stat-subtitle">
                Kräver uppmärksamhet
              </div>
            </div>
          )}
        </div>

        {/* Charts and Analysis */}
        <Row className="mb-4">
          <Col lg={6}>
            <div className="modern-card">
              <div className="p-4">
                <div className="d-flex align-items-center mb-3">
                  <BarChart3 size={20} style={{ color: 'var(--bs-primary)' }} className="me-2" />
                  <h5 style={{ margin: 0, fontWeight: '600' }}>Fakturastatistik</h5>
                </div>
                <div style={{ height: '300px' }}>
                  <Bar data={chartData} options={chartOptions} />
                </div>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className="modern-card">
              <div className="p-4">
                <div className="d-flex align-items-center mb-3">
                  <Clock size={20} style={{ color: 'var(--bs-primary)' }} className="me-2" />
                  <h5 style={{ margin: 0, fontWeight: '600' }}>Status översikt</h5>
                </div>

                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ fontWeight: '500' }}>Väntande fakturor</span>
                    <Badge bg="warning">{stats.pending_invoices}</Badge>
                  </div>
                  <div className="progress mb-3" style={{ height: '8px' }}>
                    <div
                      className="progress-bar bg-warning"
                      style={{
                        width: `${stats.total_invoices > 0 ? (stats.pending_invoices / stats.total_invoices) * 100 : 0}%`
                      }}
                    ></div>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ fontWeight: '500' }}>Betalda fakturor</span>
                    <Badge bg="success">{stats.paid_invoices}</Badge>
                  </div>
                  <div className="progress mb-3" style={{ height: '8px' }}>
                    <div
                      className="progress-bar"
                      style={{
                        width: `${stats.total_invoices > 0 ? (stats.paid_invoices / stats.total_invoices) * 100 : 0}%`,
                        backgroundColor: 'var(--bs-primary)'
                      }}
                    ></div>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ fontWeight: '500' }}>Förfallna fakturor</span>
                    <Badge bg="danger">{stats.overdue_invoices}</Badge>
                  </div>
                  <div className="progress mb-3" style={{ height: '8px' }}>
                    <div
                      className="progress-bar bg-danger"
                      style={{
                        width: `${stats.total_invoices > 0 ? (stats.overdue_invoices / stats.total_invoices) * 100 : 0}%`
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* Invoices Table */}
        <div className="modern-card">
          <div className="p-4">
            <div className="d-flex align-items-center justify-content-between mb-4">
              <div>
                <h5 style={{ margin: 0, fontWeight: '600' }}>Fakturor från {supplier.name}</h5>
                <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                  {invoices.length > 0
                    ? `${invoices.length} fakturor och betalningshistorik`
                    : 'Inga fakturor registrerade än'
                  }
                </p>
              </div>
              <Button
                variant="primary"
                style={{
                  background: 'var(--bs-primary)',
                  borderColor: 'var(--bs-primary)',
                  padding: '0.5rem 1rem',
                  fontWeight: '600'
                }}
                onClick={() => history.push(`/${companyId}/create-supplier-invoices`)}
              >
                <FileText size={16} className="me-2" />
                Ny faktura
              </Button>
            </div>

            {invoices.length > 0 ? (
              <SupplierInvoiceTable
                data={invoices}
                handleEdit={handleEditInvoice}
                handleDelete={handleDeleteInvoice}
              />
            ) : (
              <div className="text-center py-5">
                <div
                  className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: '#f3f4f6',
                    color: '#6b7280'
                  }}
                >
                  <FileText size={24} />
                </div>
                <h6 style={{ color: '#374151', marginBottom: '0.5rem' }}>Inga fakturor än</h6>
                <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                  Det finns inga registrerade fakturor från denna leverantör.
                </p>
                <Button
                  variant="outline-primary"
                  style={{
                    borderColor: 'var(--bs-primary)',
                    color: 'var(--bs-primary)',
                    padding: '0.5rem 1rem'
                  }}
                  onClick={() => history.push(`/${companyId}/create-supplier-invoices`)}
                >
                  <FileText size={16} className="me-2" />
                  Skapa första fakturan
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Edit Supplier Modal */}
        <NewSupplierModal
          key={modalKey}
          show={showEditModal}
          handleClose={handleCloseModal}
          handleUpdate={handleUpdate}
          initialSupplierData={supplier}
        />
      </div>
    </>
  );
};

export default SupplierDetails;
